<script setup lang="ts">
import TextInput from "@/components/Reusables/Inputs/TextInput.vue";
import FilterBox from "@/components/Reusables/FiltersV2/FilterBox.vue";
import { useGetProjectListFiltersQuery } from "@/graphql";
import { computed, ref, watch } from "vue";
import Filter from "@/types/Utils/Filter";
import FavoriteStarButton from "@/components/Reusables/Buttons/FavoriteStarButton.vue";
import { LayoutGrid, List } from "@lucide/vue";
import NewProjectButton from "../components/NewProjectButton.vue";
import { ProjectFilterInput } from "@/api/types/Project";
import constructProjectListFilters from "../utils/constructProjectListFilters";

const FILTERS_STORAGE_KEY = "gaiup-projectList-activeFilters";

const props = defineProps<{
  hasAccessAllProjects?: boolean;
  loading?: boolean;
  searchString: string;
  view: "list" | "grid";
}>();

const emit = defineEmits<{
  (e: "reload"): void;
  (e: "viewChange", view: "list" | "grid"): void;
  (e: "update", filters: ProjectFilterInput): void;
  (e: "update:searchString", value: string): void;
}>();

const searchStringModel = computed({
  get: () => props.searchString,
  set: (value: string) => emit("update:searchString", value),
});
// `filters` is the read-only catalog of available options (built from the query). The V2
// FilterBox drives selection through `activeFilters` ({ group: { id: boolean } }), which —
// together with `favoritesActive` — is the single source of truth for what is selected.
const filters = ref<Array<Filter>>([]);
const activeFilters = ref<Record<string, Record<string, boolean>>>({});
const favoritesActive = ref<boolean>(false);

// Order the panel renders the groups in. `favorites` is surfaced separately via the slot.
const PROJECT_GROUP_ORDER = ["calculationType", "country", "stage", "role", "type"];

const groupLabels: Record<string, string> = {
  calculationType: "Calculation type",
  country: "Country",
  stage: "Stage",
  role: "Role",
  type: "Type",
};

const filterGroups = computed<Record<string, Filter[]>>(() => {
  const groups: Record<string, Filter[]> = {};
  PROJECT_GROUP_ORDER.forEach((key) => (groups[key] = []));
  filters.value.forEach((filter) => {
    if (filter.filterGroup === "favorites") return;
    (groups[filter.filterGroup] ??= []).push(filter);
  });
  return groups;
});

const queryContext = computed(() =>
  props.hasAccessAllProjects
    ? {
        headers: {
          "x-hasura-role": "accessAllProjects",
        },
      }
    : undefined
);

const { onResult } = useGetProjectListFiltersQuery({
  enabled: computed(() => props.hasAccessAllProjects !== undefined),
  context: queryContext,
});

const getFilterInput = (): ProjectFilterInput => {
  const buildingTypeLabels: string[] = [];
  const calculationTypeLabels: string[] = [];
  const countries: string[] = [];
  const projectStatuses: number[] = [];
  const roles: string[] = [];

  filters.value.forEach((filter) => {
    if (filter.filterGroup === "favorites") return;
    if (!activeFilters.value[filter.filterGroup]?.[String(filter.id)]) return;

    switch (filter.filterGroup) {
      case "calculationType":
        calculationTypeLabels.push(filter.value as string);
        break;
      case "country":
        countries.push(filter.value as string);
        break;
      case "stage":
        projectStatuses.push(filter.value as number);
        break;
      case "role":
        roles.push(filter.value as string);
        break;
      case "type":
        buildingTypeLabels.push(filter.label);
        break;
    }
  });

  return {
    buildingTypeLabels,
    calculationTypeLabels,
    countries,
    isFavorite: favoritesActive.value,
    roles,
    projectStatuses,
  };
};

const handleGridListButtonClick = () => {
  emit("viewChange", props.view === "grid" ? "list" : "grid");
};

onResult(async (result) => {
  if (result.data?.projectCalculation) {
    const buildingTypes = result.data.buildingType.map((type) => type.buildingType.label);
    const calculationTypes = new Set(result.data.projectCalculation.map((calc) => calc.calculationType?.label));
    const countries = new Set(result.data.projectLocation.map((loc) => loc.country));
    const roles = result.data.projectMembership
      .filter((x) => !!x.role)
      .sort((a, b) => (b.membershipRole?.level ?? 0) - (a.membershipRole?.level ?? 0))
      .map((x) => x.role!);

    if (props.hasAccessAllProjects) {
      roles.push("accessAllProjects");
    }

    const stages = result.data.projectStatus.filter((x) => !!x.projectStatus).map((status) => status.projectStatus!.id);

    const builtFilters = await constructProjectListFilters(buildingTypes, stages, countries, calculationTypes, roles);

    // Restore saved selections from localStorage into the activeFilters model + favorites flag.
    const restored: Record<string, Record<string, boolean>> = {};
    let restoredFavorites = false;
    try {
      const stored = localStorage.getItem(FILTERS_STORAGE_KEY);
      if (stored) {
        const savedActiveFilters: Array<{ filterGroup: string; value: Filter["value"] }> = JSON.parse(stored);
        builtFilters.forEach((filter) => {
          const isActive = savedActiveFilters.some(
            (saved) => saved.filterGroup === filter.filterGroup && saved.value === filter.value
          );
          if (!isActive) return;
          if (filter.filterGroup === "favorites") {
            restoredFavorites = true;
            return;
          }
          (restored[filter.filterGroup] ??= {})[String(filter.id)] = true;
        });
      }
    } catch {
      // corrupted data — use defaults
    }

    filters.value = builtFilters;
    activeFilters.value = restored;
    favoritesActive.value = restoredFavorites;
    // The [activeFilters, favoritesActive] watcher emits the initial "update" exactly once
    // off this assignment, so the project list fetches with the correct initial filters.
  }
});

const handleShowFavorites = () => {
  favoritesActive.value = !favoritesActive.value;
};

// Persist the current selection to localStorage and emit the resolved filter input.
const persistAndEmit = () => {
  try {
    const saved: Array<{ filterGroup: string; value: Filter["value"] }> = [];
    filters.value.forEach((filter) => {
      if (filter.filterGroup === "favorites") return;
      if (activeFilters.value[filter.filterGroup]?.[String(filter.id)]) {
        saved.push({ filterGroup: filter.filterGroup, value: filter.value });
      }
    });
    if (favoritesActive.value) saved.push({ filterGroup: "favorites", value: true });
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(saved));
  } catch {
    // storage write failed — non-critical
  }
  emit("update", getFilterInput());
};

// Fires on the initial restore (one emit) and on every user toggle thereafter. FilterBox
// mutates `activeFilters` in place, so the watch must be deep.
watch([activeFilters, favoritesActive], persistAndEmit, { deep: true });
</script>

<template>
  <div class="flex w-full flex-col items-center gap-4 md:flex-row">
    <div class="flex w-full items-center gap-2">
      <TextInput
        v-model:value="searchStringModel"
        color="primary"
        placeholder="Search for Project"
        :is-search="true"
        size="sm">
        <!-- Sort control rendered as a ghost icon inside the search bar (see ProjectsPage slot) -->
        <template #actions>
          <slot />
        </template>
      </TextInput>

      <FilterBox
        v-model:active-filters="activeFilters"
        title="Filter projects"
        :search="true"
        :filter-groups="filterGroups"
        :group-labels="groupLabels"
        :extra-active-count="favoritesActive ? 1 : 0"
        @clear="favoritesActive = false">
        <!-- Favourite button -->
        <div
          :class="[
            'rounded-8 t-small focus-ring hover:border-app-yellow-300 hover:bg-app-yellow-50 flex h-9 w-full cursor-pointer items-center justify-between gap-2 border px-1 pr-3 leading-none',
            favoritesActive
              ? 'border-app-yellow-300 bg-app-yellow-50 border'
              : 'bg-slate-0 hover:bg-app-yellow-50 border-slate-200',
          ]"
          role="button"
          tabindex="0"
          :aria-pressed="favoritesActive"
          @click="handleShowFavorites"
          @keydown.enter="handleShowFavorites"
          @keydown.space.prevent="handleShowFavorites">
          <FavoriteStarButton
            :animate="false"
            :is-favorite="favoritesActive" />
          <span>Favorites only</span>
        </div>
      </FilterBox>
    </div>

    <div class="ml-auto flex items-center">
      <!-- Grid/list view toggle — ghost icon button matching the new toolbar design -->
      <button
        v-tooltip="{
          text: view === 'grid' ? 'List View' : 'Grid View',
          variant: 'label',
          placement: 'bottom',
        }"
        type="button"
        :aria-label="view === 'grid' ? 'List view' : 'Grid view'"
        class="rounded-8 bg-slate-0 text-type-default focus-ring interactive-active duration-fast ease-standard p-px-8 ring-slate-250 mr-2 hidden h-fit shrink-0 items-center justify-center ring-1 transition-all hover:bg-slate-100 hover:ring-slate-300 md:inline-flex"
        @click="handleGridListButtonClick">
        <component
          :is="view === 'grid' ? List : LayoutGrid"
          class="icon-sm shrink-0"
          :stroke-width="1.5"
          color="var(--color-app-blue-800)"
          fill="var(--color-app-blue-100)" />
      </button>

      <!-- Create project — labeled ghost chip matching the new toolbar design. Asks for the country
           first, because that decides which steps the wizard has. -->
      <NewProjectButton />
    </div>
  </div>
</template>
